"use client";

import Link from "next/link";
import Box from "@mui/material/Box";
import Container from "@mui/material/Container";
import Grid from "@mui/material/Grid";
import Image from "next/image";
import { H6, Paragraph } from "src/lib/components/salify-next-layout/default/components/Typography";
import { FlexBox } from "src/lib/components/salify-next-layout/default/components/flex-box";
import { resolveIconCssSize } from "src/lib/components/v2/icon-utils";
import { toAssetImages } from "src/lib/utils";
import { FooterComponentProps, FooterLink, FooterSocialLink } from "../types";

const clampOpacity = (value?: number) => {
  const num = Number(value);
  if (Number.isNaN(num)) return 0.75;
  return Math.min(1, Math.max(0, num));
};

const LinkList = ({ links = [] }: { links?: FooterLink[] }) =>
  links.map((item, index) => (
    <Paragraph key={`${item.label}-${index}`} py={{ xs: 0.4, sm: 0.5 }}>
      <Link
        href={item.url || "#"}
        target={item.target || "_self"}
        style={{
          color: "#cccccc",
          textDecoration: "none",
          fontSize: "14px",
          display: "block",
          lineHeight: "1.5",
        }}
      >
        {item.label}
      </Link>
    </Paragraph>
  ));

const SocialLinks = ({ items = [] }: { items?: FooterSocialLink[] }) => {
  if (!items.length) return null;

  return (
    <FlexBox className="flex" sx={{ gap: { xs: 1, sm: 0.5 } }}>
      {items.map((item, index) => {
        const iconAsset = resolveIconCssSize(item.icon, {
          width: "20px",
          height: "20px",
        });
        const label = item.displayText || item.url || "";
        const content = iconAsset.url ? (
          <Box
            component="img"
            src={toAssetImages(iconAsset.url)}
            alt={label}
            sx={{
              width: iconAsset.width || "20px",
              height: iconAsset.height || "20px",
              objectFit: "contain",
              display: "block",
            }}
          />
        ) : (
          label
        );

        if (!item.url) {
          return (
            <Box key={index} sx={{ color: "#cccccc", px: 1 }}>
              {content}
            </Box>
          );
        }

        return (
          <Link
            href={item.url}
            key={index}
            target={item.target || "_blank"}
            style={{
              color: "#cccccc",
              padding: "8px",
              minWidth: "40px",
              minHeight: "40px",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            {content}
          </Link>
        );
      })}
    </FlexBox>
  );
};

const FooterSiteLayout = (props: FooterComponentProps) => {
  if (props.visible === false) return null;

  const logoAsset = resolveIconCssSize(props.logo, {
    width: "160px",
    height: "48px",
  });
  const logoWidth = Number.parseInt(String(logoAsset.width || "160").replace("px", ""), 10) || 160;
  const logoHeight = Number.parseInt(String(logoAsset.height || "48").replace("px", ""), 10) || 48;
  const overlayOpacity = clampOpacity(props.overlayOpacity);
  const backgroundUrl = props.backgroundImage ? toAssetImages(props.backgroundImage) : undefined;
  const sections = props.sections || [];
  const mainSections = sections.slice(0, -1);
  const lastSection = sections[sections.length - 1];
  const companyInfo = props.companyInfo || {};

  return (
    <Box
      component="footer"
      bgcolor="#1a1a1a"
      color="white"
      sx={{
        position: "relative",
        backgroundImage: backgroundUrl ? `url(${backgroundUrl})` : "none",
        backgroundSize: "cover",
        backgroundPosition: "center",
        backgroundRepeat: "no-repeat",
      }}
      mb={0}
    >
      {backgroundUrl ? (
        <Box
          sx={{
            position: "absolute",
            inset: 0,
            bgcolor: `rgba(0, 0, 0, ${overlayOpacity})`,
            pointerEvents: "none",
          }}
        />
      ) : null}

      <Box sx={{ position: "relative", zIndex: 1 }}>
        <Box
          component={Container}
          sx={{
            px: { xs: 2, sm: 3, md: 4, lg: 6 },
            pt: { xs: 4, sm: 8 },
            pb: { xs: 2, sm: 4 },
          }}
          overflow="hidden"
        >
          <Grid container spacing={{ xs: 2, sm: 3 }}>
            <Grid item lg={3} md={6} sm={6} xs={12}>
              <Link href="/">
                {logoAsset.url ? (
                  <Image
                    src={toAssetImages(logoAsset.url)}
                    alt={companyInfo.name || "Company Logo"}
                    width={logoWidth}
                    height={logoHeight}
                  />
                ) : null}
              </Link>

              <Box mt={{ xs: 2, sm: 3 }}>
                {companyInfo.name ? (
                  <Paragraph
                    py={0.6}
                    sx={{
                      color: "white",
                      fontSize: { xs: "16px", sm: "14px" },
                      fontWeight: 600,
                    }}
                  >
                    {companyInfo.name}
                  </Paragraph>
                ) : null}

                {companyInfo.address ? (
                  <Paragraph
                    py={0.6}
                    sx={{
                      color: "#cccccc",
                      fontSize: { xs: "14px", sm: "13px" },
                      lineHeight: 1.6,
                    }}
                  >
                    {companyInfo.address}
                  </Paragraph>
                ) : null}

                {companyInfo.phone ? (
                  <Paragraph
                    py={0.3}
                    sx={{ color: "#cccccc", fontSize: { xs: "14px", sm: "13px" } }}
                  >
                    • {companyInfo.phone}
                  </Paragraph>
                ) : null}

                {companyInfo.mobile ? (
                  <Paragraph
                    py={0.3}
                    sx={{ color: "#cccccc", fontSize: { xs: "14px", sm: "13px" } }}
                  >
                    • {companyInfo.mobile}
                  </Paragraph>
                ) : null}

                {companyInfo.email ? (
                  <Paragraph
                    py={0.3}
                    sx={{ color: "#cccccc", fontSize: { xs: "14px", sm: "13px" } }}
                  >
                    • {companyInfo.email}
                  </Paragraph>
                ) : null}
              </Box>

              {props.workingHours ? (
                <Box mt={{ xs: 2, sm: 2.5 }}>
                  <H6 fontSize={{ xs: 16, sm: 14 }} mb={1} sx={{ color: "white", fontWeight: 600 }}>
                    Thời gian làm việc
                  </H6>
                  <Paragraph
                    py={0.3}
                    sx={{
                      color: "#cccccc",
                      fontSize: { xs: "14px", sm: "13px" },
                      lineHeight: 1.6,
                    }}
                    dangerouslySetInnerHTML={{ __html: props.workingHours }}
                  />
                </Box>
              ) : null}
            </Grid>

            <Grid item lg={9} md={6} sm={6} xs={12}>
              <Grid container spacing={{ xs: 1.5, sm: 2 }}>
                {mainSections.map((section, index) => (
                  <Grid item lg={4} md={12} sm={12} xs={12} key={`${section.title}-${index}`}>
                    <H6
                      fontSize={{ xs: 16, sm: 18 }}
                      mb={1.5}
                      lineHeight="1"
                      sx={{ color: "white", fontWeight: 600 }}
                    >
                      {section.title}
                    </H6>
                    <LinkList links={section.links} />
                  </Grid>
                ))}

                {lastSection ? (
                  <Grid item lg={4} md={12} sm={12} xs={12}>
                    <H6
                      fontSize={{ xs: 16, sm: 18 }}
                      mb={1.5}
                      lineHeight="1"
                      sx={{ color: "white", fontWeight: 600 }}
                    >
                      {lastSection.title}
                    </H6>
                    <LinkList links={lastSection.links} />

                    <Box mt={{ xs: 2, sm: 3 }}>
                      <Paragraph
                        sx={{
                          color: "white",
                          fontSize: { xs: "16px", sm: "14px" },
                          marginBottom: 1,
                          fontWeight: 600,
                        }}
                      >
                        {props.socialConnectTitle || "Kết nối với chúng tôi"}
                      </Paragraph>
                      <SocialLinks items={props.socialLinks} />
                    </Box>
                  </Grid>
                ) : null}
              </Grid>
            </Grid>
          </Grid>
        </Box>

        <Box
          component={Container}
          sx={{
            borderTop: "1px solid #333",
            px: { xs: 2, sm: 3, md: 4, lg: 6 },
            py: { xs: 2, sm: 2 },
          }}
        >
          <Grid container spacing={{ xs: 2, sm: 3 }} alignItems="center">
            <Grid item sm={8} xs={12} textAlign={{ xs: "center", sm: "left" }}>
              <Paragraph
                sx={{
                  color: "#cccccc",
                  fontSize: { xs: "13px", sm: "14px" },
                  lineHeight: 1.5,
                }}
              >
                {props.copyright ||
                  "© 2025 Hai Anh Automation Technology Company Limited. All rights reserved."}
              </Paragraph>
            </Grid>
            <Grid item sm={4} xs={12} textAlign={{ xs: "center", sm: "right" }}>
              <Box
                display="flex"
                flexDirection={{ xs: "column", sm: "row" }}
                justifyContent={{ xs: "center", sm: "flex-end" }}
                gap={{ xs: 1, sm: 2 }}
              >
                {(props.policyLinks || []).map((policy, index) => (
                  <Link
                    key={`${policy.label}-${index}`}
                    href={policy.url || "#"}
                    target={policy.target || "_self"}
                    style={{
                      color: "#cccccc",
                      textDecoration: "none",
                      fontSize: "13px",
                      lineHeight: "1.5",
                    }}
                  >
                    {policy.label}
                  </Link>
                ))}
              </Box>
            </Grid>
          </Grid>
        </Box>
      </Box>
    </Box>
  );
};

export default FooterSiteLayout;
